/* AudioLearn 2026 — theme.js
   Global behaviors: sticky header shadow, drawers (focus trap, Esc, focus return),
   mega-menu keyboard support, ARIA tabs, TOC expand, announcement dismiss,
   guest wishlist (localStorage). No third-party libraries. */

(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Focus trap helper ── */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, e) {
    var nodes = Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null;
    });
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ── Sticky header shadow (I-001) ── */
  var header = document.querySelector('.site-header');
  if (header) {
    var onScroll = function () { header.classList.toggle('is-scrolled', window.scrollY > 10); };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ── Drawer component (cart drawer, mobile nav) ── */
  window.customElements.define('al-drawer', class extends HTMLElement {
    connectedCallback() {
      this.trigger = null;
      this.triggers = Array.prototype.slice.call(document.querySelectorAll('[data-drawer-open="' + this.id + '"]'));
      this.overlay = this.querySelector('.drawer-overlay');
      this.panel = this.querySelector('.drawer');
      if (this.overlay) this.overlay.addEventListener('click', this.close.bind(this));
      this.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { e.stopPropagation(); this.close(); }
        if (e.key === 'Tab' && this.dataset.open === 'true') trapFocus(this.panel, e);
      }.bind(this));
      this.querySelectorAll('[data-drawer-close]').forEach(function (btn) {
        btn.addEventListener('click', this.close.bind(this));
      }, this);
      this.querySelectorAll('[data-drawer-search]').forEach(function (btn) {
        btn.addEventListener('click', this.close.bind(this));
      }, this);
      this.triggers.forEach(function (btn) {
        btn.setAttribute('aria-controls', this.id);
        btn.setAttribute('aria-expanded', 'false');
        btn.addEventListener('click', function () { this.open(btn); }.bind(this));
      }, this);
    }
    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.dataset.open = 'true';
      this.triggers.forEach(function (btn) { btn.setAttribute('aria-expanded', 'true'); });
      document.body.style.overflow = 'hidden';
      var self = this;
      window.setTimeout(function () {
        var target = self.panel.querySelector('[data-autofocus]') || self.panel.querySelector(FOCUSABLE);
        if (target) target.focus();
      }, reducedMotion ? 0 : 150);
    }
    close() {
      if (this.dataset.open !== 'true') return;
      this.dataset.open = 'false';
      this.triggers.forEach(function (btn) { btn.setAttribute('aria-expanded', 'false'); });
      document.body.style.overflow = '';
      if (this.trigger && this.trigger.focus) this.trigger.focus();
    }
  });

  /* ── Mega menu keyboard support (AH-002) ── */
  document.querySelectorAll('.mega-menu-wrap').forEach(function (wrap) {
    var trigger = wrap.querySelector('[data-mega-trigger]');
    var menu = wrap.querySelector('.mega-menu');
    if (!trigger || !menu) return;
    var pinnedOpen = false;
    var suppressHover = false;
    var suppressFocusOpen = false;

    function setMegaMenuOpen(open) {
      menu.classList.toggle('is-open', open);
      menu.hidden = !open;
      trigger.setAttribute('aria-expanded', String(open));
    }

    setMegaMenuOpen(false);

    trigger.addEventListener('click', function () {
      if (pinnedOpen) {
        pinnedOpen = false;
        suppressHover = true;
        setMegaMenuOpen(false);
      } else {
        pinnedOpen = true;
        suppressHover = false;
        setMegaMenuOpen(true);
      }
    });
    wrap.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var shouldRefocus = document.activeElement !== trigger;
        pinnedOpen = false;
        suppressHover = true;
        suppressFocusOpen = shouldRefocus;
        setMegaMenuOpen(false);
        if (shouldRefocus) trigger.focus();
      }
    });
    wrap.addEventListener('mouseenter', function () {
      if (!suppressHover) setMegaMenuOpen(true);
    });
    wrap.addEventListener('mouseleave', function () {
      suppressHover = false;
      if (!pinnedOpen) setMegaMenuOpen(false);
    });
    /* The menu also opens visually via CSS :focus-within when a keyboard
       user Tabs into it (no click/Enter needed). Sync aria-expanded to that
       same trigger so screen reader users hear "expanded" to match what's
       visibly open, not just on the click path above. */
    wrap.addEventListener('focusin', function () {
      if (suppressFocusOpen) {
        suppressFocusOpen = false;
        return;
      }
      setMegaMenuOpen(true);
    });
    wrap.addEventListener('focusout', function (e) {
      if (!wrap.contains(e.relatedTarget)) {
        pinnedOpen = false;
        suppressHover = false;
        setMegaMenuOpen(false);
      }
    });
    document.addEventListener('click', function (e) {
      if (!wrap.contains(e.target)) {
        pinnedOpen = false;
        suppressHover = false;
        setMegaMenuOpen(false);
      }
    });
  });

  /* ── Tabs (I-013, AM-004): filter pre-rendered panels ── */
  window.customElements.define('al-tabs', class extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[role="tabpanel"]'));
      this.tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { this.select(i); }.bind(this));
        tab.addEventListener('keydown', function (e) {
          var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          var next = (i + dir + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        }.bind(this));
      }, this);
    }
    select(index) {
      this.tabs.forEach(function (tab, i) {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach(function (panel, i) { panel.hidden = i !== index; });
    }
  });

  /* ── TOC expand/collapse (I-010) ── */
  document.querySelectorAll('[data-toc]').forEach(function (toc) {
    var btn = toc.querySelector('[data-toc-toggle]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var expanded = btn.getAttribute('aria-expanded') === 'true';
      toc.querySelectorAll('.toc-hidden-item').forEach(function (li) {
        li.classList.toggle('toc-hidden', expanded);
      });
      btn.setAttribute('aria-expanded', String(!expanded));
      btn.querySelector('[data-label-more]').hidden = !expanded;
      btn.querySelector('[data-label-less]').hidden = expanded;
    });
  });

  /* ── Announcement bar dismissal (session cookie) ── */
  document.querySelectorAll('[data-announcement]').forEach(function (bar) {
    var key = 'al-announcement-dismissed';
    if (window.sessionStorage.getItem(key) === '1') { bar.remove(); return; }
    var btn = bar.querySelector('[data-announcement-dismiss]');
    if (btn) btn.addEventListener('click', function () {
      window.sessionStorage.setItem(key, '1');
      bar.remove();
    });
  });

  /* ── Guest wishlist (I-009) — localStorage only ── */
  var WISHLIST_KEY = 'al-wishlist';
  function getWishlist() {
    try { return JSON.parse(window.localStorage.getItem(WISHLIST_KEY) || '[]'); } catch (e) { return []; }
  }
  function setWishlist(list) {
    try { window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); } catch (e) { /* private mode */ }
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    var handle = btn.getAttribute('data-wishlist-toggle');
    var list = getWishlist();
    var idx = list.indexOf(handle);
    var active = idx === -1;
    if (active) list.push(handle); else list.splice(idx, 1);
    setWishlist(list);
    btn.setAttribute('aria-pressed', String(active));
    btn.setAttribute('aria-label', active ? btn.dataset.labelRemove : btn.dataset.labelAdd);
  });
  document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
    var active = getWishlist().indexOf(btn.getAttribute('data-wishlist-toggle')) !== -1;
    btn.setAttribute('aria-pressed', String(active));
    btn.setAttribute('aria-label', active ? btn.dataset.labelRemove : btn.dataset.labelAdd);
  });

  /* ── Decorative learner photography rotation ── */
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.querySelectorAll('[data-learner-rotation]').forEach(function (root) {
      var image = root.querySelector('[data-learner-rotation-image]');
      if (!image) return;

      var slides;
      try { slides = JSON.parse(root.getAttribute('data-learner-slides') || '[]'); } catch (e) { slides = []; }
      if (slides.length < 2) return;

      var index = root.hasAttribute('data-learner-rotation-random-start')
        ? Math.floor(Math.random() * slides.length)
        : 0;

      var hero = root.closest('.al-prod-hero');
      var courseSlides = hero ? Array.prototype.slice.call(hero.querySelectorAll('[data-hero-course-index]')) : [];
      var selectCourse = function (slideIndex) {
        courseSlides.forEach(function (courseSlide) {
          var active = Number(courseSlide.getAttribute('data-hero-course-index')) === slideIndex;
          courseSlide.classList.toggle('is-active', active);
          courseSlide.setAttribute('aria-hidden', String(!active));
          if (active) courseSlide.removeAttribute('tabindex');
          else courseSlide.setAttribute('tabindex', '-1');
        });
      };

      selectCourse(index);

      if (index > 0) {
        var initialSlide = slides[index];
        image.src = initialSlide.src;
        image.srcset = initialSlide.srcset;
        image.style.objectPosition = initialSlide.position || '';
        image.classList.toggle('is-flipped', Boolean(initialSlide.flip));
      }
      var interval = Number(root.getAttribute('data-learner-rotation-interval')) || 14000;
      window.setInterval(function () {
        index = (index + 1) % slides.length;
        var slide = slides[index];
        var preload = new Image();
        preload.sizes = image.sizes || '100vw';
        preload.srcset = slide.srcset;
        preload.src = slide.src;

        var reveal = function () {
          image.classList.add('is-rotating');
          window.setTimeout(function () {
            image.src = slide.src;
            image.srcset = slide.srcset;
            image.style.objectPosition = slide.position || '';
            image.classList.toggle('is-flipped', Boolean(slide.flip));
            selectCourse(index);
            image.classList.remove('is-rotating');
          }, 180);
        };

        if (preload.decode) preload.decode().then(reveal).catch(reveal);
        else {
          preload.onload = reveal;
          preload.onerror = function () {};
        }
      }, interval);
    });
  }

  /* ── Product finder → Shopify search URL (I-020) ── */
  document.querySelectorAll('[data-product-finder]').forEach(function (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var subjectInput = form.querySelector('[name="q"]');
      var subject = subjectInput.value.trim().replace(/["\\]/g, ' ').replace(/\s+/g, ' ');
      if (!subject) {
        subjectInput.focus();
        return;
      }
      var q = '(title:"' + subject + '" OR tag:"' + subject + '")';
      var params = new URLSearchParams();
      params.set('q', q);
      params.set('type', 'product');
      var format = form.querySelector('[data-finder-format][aria-pressed="true"]');
      if (format) params.append('filter.p.m.audiolearn.format', format.dataset.finderFormat);
      window.location.href = form.dataset.searchUrl + '?' + params.toString();
    });
    form.querySelectorAll('[data-finder-format]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var pressed = btn.getAttribute('aria-pressed') === 'true';
        form.querySelectorAll('[data-finder-format]').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
        btn.setAttribute('aria-pressed', String(!pressed));
      });
    });
  });
})();
